import type { FieldDefinition } from '@src/utils/fieldDefinitionUtils';

export const otcForwardFieldDefinitions = {
  counterparty: {
    title: 'Counterparty',
    tooltip:
      'The counterparty to the contract.  This list contains counterparties with OTC Market Accounts (OTCDerivatives)',
    columnTooltip: 'The counterparty to the contract',
  },
  marketAccount: {
    title: 'Market Account',
    tooltip: 'The Derivatives Market Account for the counterparty',
    columnTooltip: 'The Derivatives Market Account for the counterparty',
  },
  tradeDate: {
    title: 'Trade Date',
    tooltip: 'Initial booking date for the contract',
  },
  effectiveDate: {
    title: 'Effective Date',
    tooltip: 'Date on which the contract becomes active',
  },
  side: {
    title: 'Side',
    tooltip: 'Traded side of the contract booked in risk',
  },
  unitNotional: {
    title: 'Unit Notional',
    tooltip:
      'The amount of notional for a single unit quantity of this contract. You can then trade any amount when booked.',
  },
  notionalAmount: {
    title: 'Notional',
    tooltip: 'The amount of exposure in quote asset for this contract',
  },
  payoffType: {
    title: 'Payoff Type',
    tooltip:
      'Whether the contract delivers the P&L on the contract in fiat or stablecoins (LINEAR) or in underlying asset (INVERSE)',
  },
  settlementType: {
    title: 'Settlement Type',
    tooltip: 'Whether this is a financial / cash-settled derivative or physically-settled.',
  },
  index: {
    title: 'Index',
    tooltip:
      'The index that is used as a reference price when computing delivery quantities, e.g. BRRNY for BTC underliers',
  },
  underlying: {
    title: 'Underlying',
    tooltip: 'The underlying asset that is referenced for contract P&L, and also delivered for inverses',
  },
  quantity: {
    title: 'Quantity',
    tooltip: 'Number of contracts, in Unit Notional, absolute amount of underlying asset, or HomeCurrency',
  },
  quoteAsset: {
    title: 'Quote Asset',
    tooltip: 'The asset in which we express the price of the underlier',
  },
  deliveryAsset: {
    title: 'Delivery Asset',
    tooltip:
      "The asset that will be delivered at expiration; it may be fiat or the stablecoin equivalent of the asset's current index price if linear",
  },
  pricingMode: {
    title: 'Pricing Mode',
    tooltip: 'Whether price is absolute, all-in value (Absolute), or spread-to-fixing (Spread-to-Fixing)',
  },
  forwardPrice: {
    title: 'Forward Price',
    tooltip:
      'The price paid to enter into the forward; if mode is Spread-to-Fixing, this is an estimated price based on most recent fixing.',
  },
  /** OTC blotters: combined strike (options) and forward price (forwards). Not used on forward-only booking forms. */
  strikeFwdPrice: {
    title: 'Strike / Fwd Price',
    tooltip:
      'For forwards, the agreed booking price for delivery at expiry. For vanilla options, the strike price is shown unless it is a pending Delta Strike type, in which case the Delta Strike is shown.',
  },
  spread: {
    title: 'Spread (bps)',
    tooltip: 'For spread-to-fixing type deals, the spread over the Index',
  },
  initialMarginPercent: {
    title: 'Initial Margin %',
    tooltip:
      'Override to the default Initial Margin % with this counterparty in case Dealer offers a break or requires a premium on IM for this particular deal.',
  },
  initialMarginCurrency: {
    title: 'Initial Margin Currency',
    tooltip: 'If multiple accepted collateral assets in the CSA, the selected asset to use for margin purposes.',
  },
  maintenanceMarginPercent: {
    title: 'Maintenance Margin %',
    tooltip:
      'For OTC derivatives, % of notional that needs to be maintained as equity to avoid a margin call, e.g. 1% of position value. In auto-liquidation mode, this can be used as a warning threshold, or it can be set equal to Liquidation Margin % to indicate no warning.',
  },
  marginCallPercent: {
    title: 'Margin Call %',
    tooltip:
      'Collateral health % at which a warning-level margin call is triggered to restore sufficient collateral levels. If not specified, margin calls are immediately raised when collateral value is below Maintenance Margin %, and position will be liquidated immediately if not cured.',
  },
  liquidationMarginPercent: {
    title: 'Margin Liquidation %',
    tooltip: 'For OTC derivatives, % of notional that needs to be maintained as equity to avoid auto-liquidation',
  },
  releaseMarginPercent: {
    title: 'Margin Release %',
    tooltip:
      'For OTC derivatives, if equity exceeds this level, the customer may be allowed to withdraw excess collateral',
  },
  initialMarginAmount: {
    title: 'Initial Margin Amount',
    tooltip:
      'Based on notional amount and initial margin % in force, the amount of margin that must be posted, adjusted for any haircuts',
  },
  postedMargin: {
    title: 'Posted Margin',
    tooltip: 'Amount of collateral asset posted as margin for this OTC position',
  },
  initialMarginAmountHomeCcy: {
    title: 'Initial Margin Amount ($HomeCcy)',
    tooltip:
      'Based on notional amount and initial margin % in force, the amount of margin that must be posted in $HomeCurrency, adjusted for any haircuts',
  },
  commission: {
    title: 'Commission',
    tooltip: 'Commission paid to enter into this contract, as bps on top of the forward price, if any',
  },
  commissionCurrency: {
    title: 'Commission Ccy',
    tooltip: 'Currency accepted for payment of the commission, if any',
  },
  commissionCalculationType: {
    title: 'Commission Calculation Type',
    tooltip: 'Whether the commission is computed across the whole deal period (All In) or applied daily (Daily)',
  },
  tenor: {
    title: 'Tenor',
    tooltip: 'Relative expiration date (6M, 1Y, etc.)',
  },
  settleTime: {
    title: 'Settlement Date',
    tooltip: 'Settlement date, with corrections for date adjustments',
  },
  expiryTime: {
    title: 'Expiry Date',
    tooltip: 'Expiration date, with corrections for date adjustments',
  },
  settlementCycle: {
    title: 'Settlement Cycle',
    tooltip:
      'Window to post initial margin, deliver at end of contract, as well as the default window to settle any top-up requests for maintenance margin',
  },
  businessDayConvention: {
    title: 'Business Day Convention',
    tooltip: 'Roll convention to use if a contract date falls on a holiday',
  },
  holidayCalendars: {
    title: 'Holiday Calendars',
    tooltip: 'List of business centers (calendars), e.g. NYS, to apply when adjusting dates to business days',
  },
  leg: {
    title: 'Leg',
    tooltip: 'Order for the legs in a multi-leg instrument',
  },
  productTemplate: {
    title: 'Product Template',
    tooltip: 'Product template descriptive symbol',
  },
  suffix: {
    title: 'Suffix',
    tooltip: 'Optional tag to append to symbol, differentiating different product templates',
  },
  productDetails: {
    title: 'OTC Fields',
    tooltip: 'Product economic details for the instrument being created',
  },
  counterpartyType: {
    title: 'Counterparty Type',
    tooltip: 'Type of counterparty',
  },
} as const satisfies Record<string, FieldDefinition>;

export type OTCForwardFieldKey = keyof typeof otcForwardFieldDefinitions;
